/**
 * Centralized themed literals for the "Vigilancia Aduanera" (aduana) duel
 * visual identity: radar/scanline look, ACTA numbering, gold/green palette.
 *
 * Ported from src/pages/OperativoAduanas/index.js + index.css per ADR-0001
 * (docs/adr/0001-operativoaduanas-skin-migration.md).
 *
 * Consumed by BattleModal.jsx and OpponentPickerModal.jsx only (Slice 1b/1c).
 * No duel component may hardcode a themed literal inline — spec:
 * duel-visual-identity, "Themed Literals Are Centralized in One Module".
 */

export const ADUANA_PALETTE = {
  void: "#060b11",
  panel: "#0b1620",
  panel2: "#101f2d",
  line: "rgba(210,167,72,0.22)",
  lineStrong: "rgba(210,167,72,0.4)",
  gold: "#d2a748",
  goldDim: "#8a6c2e",
  text: "#eef1ea",
  textDim: "#7f95a6",
  danger: "#e2554a",
  success: "#59c98f",
  focus: "#7fd0ff",
};

export const ADUANA_BRAND = {
  mark: "VA",
  tagline: "8 Islas · Vigilancia Aduanera",
};

export const ADUANA_LABELS = {
  you: "Tú",
  vs: "VS",
  enterOperative: "Iniciar operativo",
  confirmTarget: "Confirmar objetivo",
  repeatOperative: "Repetir operativo",
  selectRivalTitle: "Selecciona tu rival",
  resultHeading: "Resultado",
  winnerYou: "GANADOR: TÚ",
  // BattleModal (Slice 1b) additions — keep sourced from this module so no
  // themed literal is hardcoded inline in BattleModal.jsx.
  challengeTag: "Canal cifrado · Sector 8 Islas",
  challengeTitle: "Operativo de Intercepción",
  acceptAction: "Aceptar operativo",
  declineAction: "Rechazar",
  prepareHeading: "¡Prepárate!",
  closeAction: "Cerrar",
  nextAction: "Registrar respuesta",
  finishAction: "Cerrar operativo",
  reviewAction: "Revisar actas",
  reviewHeading: "Revisión de actas",
  backToResultAction: "Volver al resultado",
  noAnswerLabel: "Sin registro",
  pointsAwardedLabel: "Puntos de guardia obtenidos",
  drawHeading: "Empate operativo",
  winHeading: "Interceptación exitosa",
  loseHeading: "Objetivo evadido",
  // ERROR / WAITING_OPPONENT branches (Slice 2b) — spec: duel-result-integrity.
  errorHeading: "Transmisión interrumpida",
  errorSubtext:
    "No se pudo cerrar el operativo. El registro sigue intacto, puedes reintentar el cierre.",
  waitingHeading: "Esperando al rival",
  waitingSubtext: "Tu parte del operativo quedó registrada. El resultado se define cuando el rival termine.",
  myScoreLabel: "Tu puntaje",
  myTimeLabel: "Tu tiempo",
  rankLabel: "Rango",
  // OpponentPickerModal (Slice 1c) additions — same rule: no themed literal
  // hardcoded inline in OpponentPickerModal.jsx.
  newChallengeTag: "Nuevo operativo",
  newChallengeTitle: "Elige el sector",
  loadingCoursesLabel: "Cargando cursos...",
  noCoursesLabel: "No tienes cursos disponibles.",
  backToCoursesAction: "← Volver a sectores",
  loadingRivalsLabel: "Cargando rivales...",
  noRivalsLabel: "No hay rivales disponibles en este sector.",
  categoryLabel: "Selecciona tema",
  allCategoriesOption: "Todos los temas (General)",
  numQuestionsLabel: "Cantidad de preguntas",
  matchDurationLabel: "Modalidad de partida",
  durationCorta: "Corta",
  durationMedia: "Media",
  durationLarga: "Larga",
  tiebreakRuleText: "Regla: Si hay empate en aciertos, gana quien responde en menor tiempo total.",
  challengeAction: "Desafiar",
  cancelAction: "Cancelar",
  loadMoreAction: "Cargar más",
  loadingMoreLabel: "Cargando...",
  sendingLabel: "Enviando desafío...",
  challengeSentHeading: "¡Desafío enviado!",
  rivalLabel: "Rival:",
  courseLabel: "Curso:",
  expiresLabel: "Expira:",
  retryAction: "Reintentar",
  pendingOutgoingHeading: "Operativo enviado",
  pendingOutgoingSubtext: "Estás esperando que el rival responda a tu desafío.",
  // DuelsPage (Slice 2) additions — spec: duel-visual-identity, "New /duels
  // page components comply". `staleChallengeNotice` is reserved for Slice 3
  // (stale/invalid activeChallengeId recovery banner, design.md D6) and is
  // added here alongside its siblings so the module stays the single source
  // of truth, even though it is not yet consumed by any component.
  duelsPageTag: "Sala de operativos",
  duelsPageTitle: "Tus Desafíos 1v1",
  comingSoonTag: "Sala de operativos",
  comingSoonTitle: "Próximamente disponible",
  comingSoonSubtitle:
    "Estamos calibrando el sistema de duelos 1v1 y operativos de conocimiento. Muy pronto podrás desafiar a tus compañeros de sector.",
  comingSoonBackAction: "Volver al inicio",
  duelsPageSubtitle:
    "Duelos de conocimiento contra tus compañeros. Preguntas idénticas, un solo intento cada uno — gana quien acierte más, rápido.",
  loadingDuelsLabel: "Cargando desafíos...",
  duelsLoadErrorLabel: "No se pudieron cargar tus desafíos. Intenta nuevamente.",
  noPendingDuelsLabel: "Sin operativos en curso",
  noPendingDuelsSubtext: "No tienes desafíos pendientes. Abrí un sector y desafía a un rival.",
  newChallengeAction: "Nuevo desafío",
  staleChallengeNotice: "El operativo seleccionado ya no está disponible. Volviste a la lista.",
  activeOpsTickerOne: "1 operativo activo",
  activeOpsTickerMany: "{count} operativos activos",
  activeOpsTickerNone: "Sin operativos activos",
  // duel-history-provisional (Phase 1) additions — spec: duel-history-provisional,
  // "All Copy and Styling Come From the Theme". Consumed by DuelsPage's tab
  // rail and DuelsHistoryList (Phase 2/3), added here now so this module
  // stays the single source of truth for the whole slice.
  activeTabLabel: "Activos",
  historyTabLabel: "Historial",
  // duels-active-hierarchy additions — urgency grouping for the "Activos"
  // tab (DuelsList): every row is bucketed as "your turn to act" or
  // "waiting on the rival" (derived from status/role/myAttempt, never
  // fabricated) so the list reads by priority instead of a flat grid.
  unknownRivalLabel: "Rival",
  activeSectionYourTurnLabel: "Tu turno",
  activeSectionWaitingLabel: "Esperando rival",
  activeYourTurnLabel: "Tu turno",
  activeWaitingLabel: "Esperando",
  respondChallengeAction: "Responder desafío",
  playNowAction: "Jugar ahora",
  viewOperativeAction: "Ver estado",
  historySubtitle: "Registro de operativos cerrados.",
  noHistoryLabel: "Sin operativos registrados",
  noHistorySubtext: "Aún no cerraste ningún operativo. Los resultados aparecerán aquí.",
  historyMockNotice:
    "Vista previa: datos de muestra mientras se conecta el registro real de operativos.",
  // duel-history-provisional (Phase 3) additions — DuelsHistoryList's stats strip
  // (design.md D7, 5 of 7 get_user_stats fields) and result-chip captions
  // (design.md D4, __mockOnly.result) must not hardcode copy inline — spec:
  // duel-history-provisional, "All Copy and Styling Come From the Theme".
  historyStatTotalLabel: "Operativos",
  historyStatWinsLabel: "Ganados",
  historyStatLossesLabel: "Perdidos",
  historyStatWinrateLabel: "Efectividad",
  historyStatStreakLabel: "Racha",
  historyResultWonLabel: "Ganado",
  historyResultLostLabel: "Perdido",
  historyResultDrawLabel: "Empate",
};

/**
 * Formats the "ACTA NN" numbering used throughout the skin, zero-padded to
 * two digits (e.g. formatActaNumber(3) -> "ACTA 03").
 */
export function formatActaNumber(n) {
  return `ACTA ${String(n).padStart(2, "0")}`;
}

/**
 * Formats the real-progress copy shown while a duel attempt is in progress.
 * Sourced only from server-reported counts (answered/total) — never a
 * fabricated correctness verdict. spec: duel-visual-identity, "Progress
 * Display Shows Real Question Count, Never Fabricated Correctness".
 */
export function formatActaProgress(answered, total) {
  return `${formatActaNumber(answered)} REGISTRADA · ${answered}/${total}`;
}

/**
 * Formats a unix-seconds timestamp as "DD.MM.YY" for the history ledger
 * (design.md — duel-history-provisional Interfaces/Contracts). Hand-formatted
 * with UTC getters (never `toLocaleDateString`) so output is deterministic
 * regardless of the host/CI timezone.
 */
export function formatOperativeDate(unixSeconds) {
  if (!unixSeconds || isNaN(Number(unixSeconds))) return "—";
  const date = new Date(Number(unixSeconds) * 1000);
  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const year = String(date.getUTCFullYear()).slice(-2);
  return `${day}.${month}.${year}`;
}

const aduanaTheme = {
  palette: ADUANA_PALETTE,
  brand: ADUANA_BRAND,
  labels: ADUANA_LABELS,
  formatActaNumber,
  formatActaProgress,
  formatOperativeDate,
};

export default aduanaTheme;
